import { BookingChainService } from '@services/api/booking/booking-chain.service';
import { BookingChain } from '@services/api/booking/booking-chain.model';
import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { LogService } from '@services/util/log.service';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { inject, Injectable } from '@angular/core';

/** Legfeljebb ennyi utántöltés a szerializálás által elvágott lánc-végekre. */
const MAX_CHAIN_RESOLVE_HOPS = 10;

@Injectable({
  providedIn: 'root'
})
export class BookingDetailService {
  private readonly bookingService = inject(BookingClientService);
  private readonly chainService = inject(BookingChainService);
  private readonly log = inject(LogService);

  getBooking(id: number): Observable<IBookingModel> {
    return this.bookingService.getItem(id);
  }

  /**
   * Save booking changes to the API
   * @param booking Full or partial booking model to save
   * @returns Observable with the updated booking data
   */
  saveBooking(booking: Partial<IBookingModel> & { id: number }): Observable<IBookingModel> {
    return this.bookingService.updateItem(booking as IBookingModel);
  }

  /**
   * A teljes láncot adja vissza. A beágyazott gráf általában elég hozzá;
   * ha a szerializálás elvágta a lánc egyik végét, a hiányzó szemet lekérdezi,
   * és a KIINDULÓ foglalásra nézve újraépíti a láncot.
   */
  resolveChain(booking: IBookingModel): Observable<BookingChain> {
    return this.resolveFrom(booking, booking.id, 0);
  }

  private resolveFrom(booking: IBookingModel, currentId: number, hop: number): Observable<BookingChain> {
    const chain = this.chainService.buildChain(booking, currentId);
    const missingId = chain.truncatedAt ? this.chainService.idFromIri(chain.truncatedAt.iri) : null;
    if (missingId === null || hop >= MAX_CHAIN_RESOLVE_HOPS) {
      return of(chain);
    }
    return this.bookingService.getItem(missingId).pipe(
      switchMap((missing) => this.resolveFrom(missing, currentId, hop + 1)),
      map((deeper) => (deeper.links.length > chain.links.length ? deeper : chain)),
      catchError((error) => {
        this.log.warn('Chain resolution failed, returning partial chain', error);
        return of(chain);
      })
    );
  }
}
